import store from "../store";
import router from "../routes";
import i18n from "../i18n";

export const themedRoutes = [
  "listing",
  "listing-map",
  "listing-details",
  "listing-reservation",
  "reservation-success",
  "reservation-cancellation",
];

export const currentConfig = () => {
  let {
    defaultConfig,
    currentConfig: currentConfigValue,
  } = store.state.config;
  const currentRoute = router.currentRoute;
  if (!themedRoutes.includes(currentRoute.name)) {
    return defaultConfig;
  }

  return currentConfigValue || defaultConfig;
};

const getDefaultConfig = () => store.state.config.defaultConfig || {};

export const currentConfigKey = (key, fallback = null) => {
  let { defaultConfig } = store.state.config;
  const getNested = (obj, path) =>
    path.split(".").reduce((acc, part) => acc?.[part], obj);

  return (
    getNested(currentConfig(), key) ?? getNested(defaultConfig, key) ?? fallback
  );
};


export const getPropertyPrimaryEmail = (property) => {
  const emails = property?.emails || [];
  const primary = emails.find(
    ({ emailType }) => emailType?.code?.toLowerCase() === "primary"
  )?.email;
  return primary || emails[0]?.email || "";
};

const escapeHtml = (value) =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const hotelEmailMailto = (hotelEmail) => {
  const email = String(hotelEmail || "").trim();
  if (!email) {
    return "";
  }

  return `<a href="mailto:${escapeHtml(email)}">${escapeHtml(email)}</a>`;
};

export const getNoAvailabilityMessage = ({
  checkIn,
  checkOut,
  hotelEmail = "",
} = {}) => {
  const defaultKey = "listing.noAvailability";
  const configuredKey = currentConfigKey("noAvailabilityTranslationKey");
  let key = defaultKey;

  if (typeof configuredKey === "string" && configuredKey.trim()) {
    const trimmed = configuredKey.trim();
    const fallbackLocale = i18n.fallbackLocale || "en";
    if (i18n.te(trimmed) || i18n.te(trimmed, fallbackLocale)) {
      key = trimmed;
    }
  }

  const variables = { checkIn, checkOut, hotelEmail: hotelEmailMailto(hotelEmail) };
  const translation = i18n.t(key, variables);
  if (translation === key && key !== defaultKey) {
    return i18n.t(defaultKey, variables);
  }

  return translation;
};

const setFavicon = (url) => {
  let link = document.querySelector("link[rel~='icon']");
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.href = url;
};

const setPageTitle = (title) => {
  document.title = title || currentConfigKey("title") || "Gloveler";
};

const setMetaDescription = (content) => {
  let el = document.querySelector('meta[name="description"]');
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", "description");
    document.head.appendChild(el);
  }
  el.setAttribute("content", content || "");
};

const setMetaProperty = (property, content) => {
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content || "");
};

const setMetaName = (name, content) => {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content || "");
};

const firstNonEmpty = (...values) =>
  values.find((value) => typeof value === "string" && value.trim())?.trim() ||
  "";

const resolveSeoFields = (config = {}) => {
  const defaults = getDefaultConfig();
  const title = firstNonEmpty(
    config.seoTitle,
    config.title,
    defaults.seoTitle,
    defaults.title,
  );
  const description = firstNonEmpty(
    config.seoDescription,
    defaults.seoDescription,
  );
  const ogTitle = firstNonEmpty(config.seoOgTitle, title);
  const ogDescription = firstNonEmpty(config.seoOgDescription, description);
  const ogImage = firstNonEmpty(
    config.seoOgImage,
    config.logo,
    defaults.seoOgImage,
    defaults.logo,
  );

  return { title, description, ogTitle, ogDescription, ogImage };
};

const applyOpenGraphTags = ({
  title,
  description,
  image,
  siteName,
}) => {
  setMetaProperty("og:type", "website");
  setMetaProperty("og:title", title);
  setMetaProperty("og:description", description);
  if (siteName) {
    setMetaProperty("og:site_name", siteName);
  }
  if (image) {
    setMetaProperty("og:image", image);
  }

  setMetaName("twitter:card", image ? "summary_large_image" : "summary");
  setMetaName("twitter:title", title);
  setMetaName("twitter:description", description);
  if (image) {
    setMetaName("twitter:image", image);
  }
};

/**
 * Apply list-level SEO (title, description, Open Graph) from config.
 */
export const applyConfigSeo = (config = {}) => {
  const {
    title,
    description,
    ogTitle,
    ogDescription,
    ogImage,
  } = resolveSeoFields(config);

  setPageTitle(title);
  setMetaDescription(description);
  applyOpenGraphTags({
    title: ogTitle,
    description: ogDescription,
    image: ogImage,
    siteName: config.title,
  });
};

const stripHtml = (html) => {
  if (!html || typeof html !== "string") return "";
  if (typeof document !== "undefined") {
    const el = document.createElement("div");
    el.innerHTML = html;
    const text = el.textContent || el.innerText || "";
    return text.replace(/\s+/g, " ").trim();
  }
  return html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
};

const truncateForMeta = (text, maxLen = 160) => {
  const t = text.replace(/\s+/g, " ").trim();
  if (!t.length) return "";
  if (t.length <= maxLen) return t;
  return `${t.slice(0, maxLen - 1)}…`;
};

/** Resolves hotel-long description HTML by locale (aligned with Property.vue). */
export const getLocalizedPropertyDescriptionHtml = (property) => {
  if (!property?.descriptions?.length) return "";
  const locale = localStorage.getItem("locale") || "en";
  const descriptions = property.descriptions.filter(
    (desc) => desc.descriptionType?.code === "hotel-long"
  );
  if (!descriptions.length) return "";
  const picked =
    descriptions.find((desc) => desc.language?.code === locale) ||
    descriptions.find((desc) => desc.language?.code === "en") ||
    descriptions[0];
  return picked?.text || "";
};

/**
 * Set document title and meta description for a property detail / checkout page.
 * Social card (OG/Twitter) title uses a booking CTA; page title stays hotel | site.
 */
export const applyPropertyPageSeo = (property) => {
  if (!property?.name) return;

  const config = currentConfig() || {};
  const defaults = getDefaultConfig();
  const siteTitle =
    firstNonEmpty(config.seoTitle, config.title, defaults.seoTitle, defaults.title) ||
    "Gloveler";
  const titleLabel = firstNonEmpty(config.title, siteTitle) || siteTitle;
  const title = `${property.name} | ${siteTitle}`;
  const plain = truncateForMeta(
    stripHtml(getLocalizedPropertyDescriptionHtml(property)),
  );
  const description =
    plain ||
    truncateForMeta(`${property.name} — ${siteTitle}`) ||
    firstNonEmpty(config.seoDescription, defaults.seoDescription);

  const ogTitle = i18n.t("property.propertySocialTitle", {
    propertyName: property.name,
    title: titleLabel,
  });

  const propertyImage = firstNonEmpty(
    property?.mainUrl,
    property?.media?.find((item) => item?.isMain && !item?.isLogo)?.url,
    ...(Array.isArray(property?.media)
      ? property.media.filter((item) => !item?.isLogo).map((item) => item?.url)
      : []),
  );
  const image = firstNonEmpty(
    propertyImage,
    config.seoOgImage,
    config.logo,
    defaults.seoOgImage,
    defaults.logo,
  );

  setPageTitle(title);
  setMetaDescription(description);
  applyOpenGraphTags({
    title: ogTitle,
    description,
    image,
    siteName: titleLabel,
  });
};

export const FOLLOW_SECTION_TITLE_FONT_STYLES = {
  "Source Sans Pro": {
    family: '"Source Sans Pro", sans-serif',
    weight: "600",
  },
  "Poppins Medium": {
    family: '"Poppins", sans-serif',
    weight: "600",
    googleFonts:
      "https://fonts.googleapis.com/css2?family=Poppins:wght@500;600&display=swap",
  },
  "Crimson Text": {
    family: '"Crimson Text", serif',
    weight: "600",
    googleFonts:
      "https://fonts.googleapis.com/css2?family=Crimson+Text:wght@400;600;700&display=swap",
  },
};

const loadedFollowSectionFonts = new Set();

const loadFollowSectionFont = (fontKey) => {
  const { googleFonts } = FOLLOW_SECTION_TITLE_FONT_STYLES[fontKey] || {};
  if (!googleFonts || loadedFollowSectionFonts.has(fontKey)) {
    return;
  }

  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = googleFonts;
  document.head.appendChild(link);
  loadedFollowSectionFonts.add(fontKey);
};

export const setTheme = async (id = "6", domain = null) => {
  let { defaultConfig } = store.state.config;
  const listChanged = String(store.state.config.currentListId) !== String(id);
  let currentConfig = store.state.config.currentConfig;

  if (!currentConfig || listChanged) {
    currentConfig = await store.dispatch("config/getConfigData", { listId: id, domain });
  }

  applyTheme(currentConfig);
  applyConfigSeo(currentConfig || defaultConfig || {});
  setFavicon(currentConfig.favicon || defaultConfig.favicon);
  return currentConfig;
};

export const applyTheme = (config) => {
  const { logoWidth, logoWidthMobile, theme, followSectionTitleFontFamily } = config;
  const root = document.documentElement;

  Object.entries(theme).forEach(([key, value]) => {
    const cssVar = `--${key.replace(/([A-Z])/g, "-$1").toLowerCase()}`;
    root.style.setProperty(cssVar, value);
  });
  root.style.setProperty(`--spinner-color`, theme.primaryColor)
  root.style.setProperty(`--logo-width`, logoWidth + "px")
  root.style.setProperty(`--logo-width-mobile`, logoWidthMobile + "px")

  const fontKey = followSectionTitleFontFamily || "Source Sans Pro";
  loadFollowSectionFont(fontKey);
  const fontStyles = FOLLOW_SECTION_TITLE_FONT_STYLES[fontKey];
  root.style.setProperty(
    "--follow-section-title-font-family",
    fontStyles.family,
  );
  root.style.setProperty(
    "--follow-section-title-font-weight",
    fontStyles.weight,
  );
};
